import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { mockPosts } from '../../mocks/posts';
import { mockPartners } from '../../mocks/partners';
import RelatedLinks from '../../components/feature/RelatedLinks';

interface Post {
  id: string;
  user_id: string;
  category: string;
  title: string;
  content: string;
  need_partner_response: boolean;
  empathy_count: number;
  created_at: string;
  profiles?: {
    nickname: string;
    user_type: string;
    partner_role?: string;
    avatar_url?: string;
  };
  reply_count?: number;
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

const categories = [
  { id: 'all', name: 'すべて', icon: 'ri-apps-line', color: 'teal', description: 'すべての投稿を表示' },
  { id: 'つぶやき・モヤモヤ', name: 'つぶやき・モヤモヤ', icon: 'ri-emotion-line', color: 'rose', description: 'ただ聞いてほしい愚痴や弱音' },
  { id: '教えて！ケアの知恵', name: '教えて！ケアの知恵', icon: 'ri-lightbulb-line', color: 'amber', description: '医療的ケアの手技や工夫' },
  { id: '保育園・社会参加', name: '保育園・社会参加', icon: 'ri-community-line', color: 'emerald', description: '保育施設や復職の悩み' },
];

export default function BoardPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [newPost, setNewPost] = useState({
    category: 'つぶやき・モヤモヤ',
    title: '',
    content: '',
    need_partner_response: false,
  });

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      const { data: postsData, error } = await query;

      if (error) throw error;

      // データベースに投稿がない場合はモックデータを使用
      if (!postsData || postsData.length === 0) {
        // モックデータをPost型に変換
        const formattedMockPosts = mockPosts.map(post => ({
          id: post.id,
          user_id: post.author.id,
          category: post.category || 'つぶやき・モヤモヤ',
          title: post.title,
          content: post.content,
          need_partner_response: false,
          empathy_count: post.empathy_count,
          created_at: post.created_at,
          profiles: {
            nickname: post.author.nickname,
            user_type: post.author.user_type,
            partner_role: post.author.partner_role,
            avatar_url: post.author.avatar_url,
          },
          reply_count: post.reply_count,
        }));
        setPosts(formattedMockPosts);
        setLoading(false);
        return;
      }

      // 各投稿のプロフィール情報と返信数を取得
      const postsWithDetails = await Promise.all(
        (postsData || []).map(async (post) => {
          let profileData = null;

          if (post.user_id) {
            // 実ユーザーの場合はDBからプロフィール取得
            const { data } = await supabase
              .from('profiles')
              .select('nickname, user_type, partner_role')
              .eq('id', post.user_id)
              .maybeSingle();
            profileData = data;
          }

          // DBにプロフィールがない場合、モックデータから補完
          if (!profileData) {
            const mockPost = mockPosts.find(m => m.id === post.id);
            if (mockPost) {
              profileData = {
                nickname: mockPost.author.nickname,
                user_type: mockPost.author.user_type,
                partner_role: mockPost.author.partner_role,
                avatar_url: mockPost.author.avatar_url,
              };
            }
          }

          // 返信数を取得
          const { count } = await supabase
            .from('replies')
            .select('*', { count: 'exact', head: true })
            .eq('post_id', post.id);

          return { 
            ...post, 
            profiles: profileData,
            reply_count: count || 0 
          };
        })
      );

      setPosts(postsWithDetails);
    } catch (error) {
      console.error('投稿の取得エラー:', error);
      // エラーの場合もモックデータを表示
      const formattedMockPosts = mockPosts.map(post => ({
        id: post.id,
        user_id: post.author.id,
        category: post.category || 'つぶやき・モヤモヤ',
        title: post.title,
        content: post.content,
        need_partner_response: false,
        empathy_count: post.empathy_count,
        created_at: post.created_at,
        profiles: {
          nickname: post.author.nickname,
          user_type: post.author.user_type,
          partner_role: post.author.partner_role,
          avatar_url: post.author.avatar_url,
        },
        reply_count: post.reply_count,
      }));
      setPosts(formattedMockPosts);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      setToast({ type: 'error', message: 'タイトルと内容を入力してください' });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('posts').insert([
        {
          user_id: user.id,
          category: newPost.category,
          title: newPost.title.trim(),
          content: newPost.content.trim(),
          need_partner_response: newPost.need_partner_response,
          empathy_count: 0,
        },
      ]);

      if (error) throw error;

      setNewPost({
        category: 'つぶやき・モヤモヤ',
        title: '',
        content: '',
        need_partner_response: false,
      });
      setShowNewPostModal(false);
      setToast({ type: 'success', message: '投稿が完了しました！' });
      fetchPosts();
    } catch (error) {
      console.error('投稿エラー:', error);
      setToast({ type: 'error', message: '投稿に失敗しました。もう一度お試しください。' });
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'つぶやき・モヤモヤ':
        return 'bg-rose-100 text-rose-700';
      case '教えて！ケアの知恵':
        return 'bg-amber-100 text-amber-700';
      case '保育園・社会参加':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-teal-50">
      {/* トースト通知 */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
          <div
            className={`flex items-center space-x-3 px-5 py-3 rounded-xl shadow-xl ${
              toast.type === 'success'
                ? 'bg-teal-600 text-white'
                : 'bg-rose-600 text-white'
            }`}
          >
            <i
              className={`text-xl ${
                toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'
              }`}
            ></i>
            <span className="text-sm font-medium whitespace-nowrap">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded-full cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                スマイル・ポート
              </span>
            </Link>
            
            {/* デスクトップメニュー */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                ホーム
              </Link>
              <Link to="/board" className="text-teal-600 font-medium whitespace-nowrap cursor-pointer">
                みんなの休憩室
              </Link>
              <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お守りマニュアル
              </Link>
              {user ? (
                <div className="flex items-center space-x-3">
                  <Link to="/mypage" className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-full hover:bg-teal-100 transition-colors cursor-pointer">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt={profile.nickname || ''} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-user-smile-line text-white text-sm"></i>
                      </div>
                    )}
                    <span className="text-sm font-medium text-teal-700">{profile?.nickname || 'ユーザー'}</span>
                  </Link>
                </div>
              ) : (
                <Link
                  to="/auth/login"
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  ログイン
                </Link>
              )}
            </div>

            {/* モバイルハンバーガーメニュー */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-gray-900"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* モバイルメニュー */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white shadow-lg rounded-b-xl">
              <div className="px-4 py-3 space-y-3">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">ホーム</Link>
                <Link to="/board" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-teal-600 cursor-pointer">みんなの休憩室</Link>
                <Link to="/manual" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">お守りマニュアル</Link>
                {user ? (
                  <Link to="/mypage" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-full hover:bg-teal-100 transition-colors cursor-pointer">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt={profile.nickname || ''} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                        <i className="ri-user-smile-line text-white text-sm"></i>
                      </div>
                    )}
                    <span className="text-sm font-medium text-teal-700">{profile?.nickname || 'ユーザー'}</span>
                  </Link>
                ) : (
                  <Link
                    to="/auth/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full text-center cursor-pointer"
                  >
                    ログイン
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* 心理的安全性メッセージ */}
      <div className="bg-gradient-to-r from-rose-100 to-purple-100 border-b border-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-center space-x-2 sm:space-x-3">
            <i className="ri-shield-check-line text-rose-500 text-xl sm:text-2xl"></i>
            <p className="text-gray-700 text-center text-xs sm:text-base">
              <strong>ここはあなたの避難所です</strong> ・ 否定されることはありません ・ 安心してお話しください
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* ヘッダー */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2 sm:mb-3">みんなの休憩室</h1>
          <p className="text-gray-600 text-sm sm:text-base lg:text-lg">一人じゃない。ここで気持ちを分かち合いましょう</p>
        </div>

        {/* カテゴリフィルター */}
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-3 sm:p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  selectedCategory === category.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center space-x-2 flex-wrap gap-2">
                  <div
                    className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full ${
                      category.id === 'all'
                        ? 'bg-teal-100'
                        : category.id === 'つぶやき・モヤモヤ'
                        ? 'bg-rose-100'
                        : category.id === '教えて！ケアの知恵'
                        ? 'bg-amber-100'
                        : 'bg-emerald-100'
                    }`}
                  >
                    <i
                      className={`${category.icon} text-lg sm:text-xl ${
                        category.id === 'all'
                          ? 'text-teal-600'
                          : category.id === 'つぶやき・モヤモヤ'
                          ? 'text-rose-600'
                          : category.id === '教えて！ケアの知恵'
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                      }`}
                    ></i>
                  </div>
                  <h3 className="font-bold text-gray-800 text-left text-xs sm:text-sm">{category.name}</h3>
                </div>
                <p className="text-xs text-gray-600 text-left hidden sm:block">{category.description}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* 投稿一覧 */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {/* 新規投稿ボタン */}
            <button
              onClick={() => {
                if (!user) {
                  navigate('/auth/login');
                  return;
                }
                setShowNewPostModal(true);
              }}
              className="w-full bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-2xl p-4 sm:p-6 hover:shadow-xl transition-all cursor-pointer"
            >
              <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                <i className="ri-add-circle-line text-2xl sm:text-3xl"></i>
                <span className="text-lg sm:text-xl font-bold whitespace-nowrap">新しい投稿をする</span>
              </div>
            </button>

            {/* ローディング */}
            {loading ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600 text-sm sm:text-base">読み込み中...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center">
                <i className="ri-chat-off-line text-4xl sm:text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-600 mb-2 text-sm sm:text-base">まだ投稿がありません</p>
                <p className="text-xs sm:text-sm text-gray-500">最初の投稿をしてみませんか？</p>
              </div>
            ) : (
              /* 投稿カード */
              posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/board/post/${post.id}`}
                  className="block bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all p-4 sm:p-6 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center space-x-2 flex-wrap gap-2">
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getCategoryStyle(post.category)}`}>
                        {post.category}
                      </span>
                      {post.need_partner_response && (
                        <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium flex items-center space-x-1">
                          <i className="ri-user-star-line"></i>
                          <span className="hidden sm:inline">パートナー回答希望</span>
                          <span className="sm:hidden">回答希望</span>
                        </span>
                      )}
                    </div>
                    <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                      {new Date(post.created_at).toLocaleDateString('ja-JP')}
                    </span>
                  </div>

                  <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-2">{post.title}</h3>
                  <p className="text-gray-600 mb-3 sm:mb-4 line-clamp-2 text-sm sm:text-base">{post.content}</p>

                  <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2 text-gray-600 min-w-0">
                      {post.profiles?.avatar_url ? (
                        <img
                          src={post.profiles.avatar_url}
                          alt={post.profiles.nickname || '投稿者'}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            post.profiles?.user_type === 'partner' ? 'bg-purple-100' : 'bg-teal-100'
                          }`}
                        >
                          <i
                            className={`text-xs sm:text-sm ${
                              post.profiles?.user_type === 'partner' ? 'ri-user-star-line text-purple-600' : 'ri-user-smile-line text-teal-600'
                            }`}
                          ></i>
                        </div>
                      )}
                      {post.profiles?.user_type === 'partner' ? (
                        (() => {
                          const partner = mockPartners.find(p => p.nickname === post.profiles?.nickname);
                          const partnerId = partner?.id || '';
                          return (
                            <>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (partnerId) {
                                    navigate(`/partner/profile/${partnerId}`);
                                  }
                                }}
                                className="text-xs sm:text-sm truncate hover:text-purple-600 hover:underline cursor-pointer transition-colors"
                              >
                                {post.profiles?.nickname || '匿名'}
                              </span>
                              {post.profiles?.partner_role && (
                                <span
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (partnerId) {
                                      navigate(`/partner/profile/${partnerId}`);
                                    }
                                  }}
                                  className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs hidden sm:inline hover:bg-purple-200 cursor-pointer transition-colors"
                                >
                                  {post.profiles.partner_role}
                                </span>
                              )}
                            </>
                          );
                        })()
                      ) : (
                        <span className="text-xs sm:text-sm truncate">{post.profiles?.nickname || '匿名'}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3 sm:space-x-4 flex-shrink-0">
                      <div className="flex items-center space-x-1 text-rose-500">
                        <i className="ri-heart-3-line text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm font-medium">{post.empathy_count}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-teal-500">
                        <i className="ri-chat-3-line text-sm sm:text-base"></i>
                        <span className="text-xs sm:text-sm font-medium">{post.reply_count || 0}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* サイドバー */}
          <div className="space-y-4 sm:space-y-6">
            {/* スマイル・パートナー紹介 */}
            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                <i className="ri-user-star-line text-purple-600 text-xl sm:text-2xl"></i>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">スマイル・パートナー</h3>
              </div>
              <p className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm">看護師、保育士、先輩ママなど、あなたに寄り添う伴走者です</p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center space-x-3 p-2 sm:p-3 bg-purple-50 rounded-xl">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-nurse-line text-purple-700 text-sm sm:text-base"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 text-sm sm:text-base">田中 美咲</p>
                    <p className="text-xs text-gray-600">看護師</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-2 sm:p-3 bg-teal-50 rounded-xl">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-parent-line text-teal-700 text-sm sm:text-base"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800 text-sm sm:text-base">佐藤 恵子</p>
                    <p className="text-xs text-gray-600">先輩ママ</p>
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-amber-50 rounded-xl">
                <p className="text-xs sm:text-sm text-gray-700">
                  <strong>見守り時間：</strong>平日 10:00〜17:00
                </p>
              </div>
            </div>

            {/* お守りマニュアルへのリンク */}
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl shadow-sm p-4 sm:p-6">
              <div className="flex items-center space-x-2 mb-2 sm:mb-3">
                <i className="ri-book-open-line text-amber-700 text-xl sm:text-2xl"></i>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">お守りマニュアル</h3>
              </div>
              <p className="text-gray-700 mb-3 sm:mb-4 text-xs sm:text-sm">よくある質問の答えがここにあります</p>
              <Link
                to="/manual"
                className="block w-full px-4 py-2 sm:py-3 bg-white text-amber-700 font-medium rounded-xl hover:shadow-md transition-all cursor-pointer whitespace-nowrap text-center text-sm sm:text-base"
              >
                マニュアルを見る
              </Link>
            </div>

            {/* 利用ガイド */}
            <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 sm:mb-3">利用ガイド</h3>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>ニックネームで安心して投稿できます</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>「わかって！」ボタンで共感を伝えましょう</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                  <span>専門家の意見が欲しい時は「パートナー回答希望」をつけてください</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 新規投稿モーダル */}
      {showNewPostModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800">新しい投稿</h2>
                <button
                  onClick={() => setShowNewPostModal(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <form onSubmit={handleSubmitPost} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">カテゴリ</label>
                  <select
                    value={newPost.category}
                    onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer text-sm sm:text-base"
                  >
                    <option value="つぶやき・モヤモヤ">つぶやき・モヤモヤ</option>
                    <option value="教えて！ケアの知恵">教えて！ケアの知恵</option>
                    <option value="保育園・社会参加">保育園・社会参加</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">タイトル</label>
                  <input
                    type="text"
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    placeholder="投稿のタイトルを入力してください"
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm sm:text-base"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">内容</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value.slice(0, 500) })}
                    rows={6}
                    placeholder="あなたの気持ちを自由に書いてください。ここは安全な場所です。"
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm sm:text-base"
                    required
                    maxLength={500}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1 text-right">{newPost.content.length}/500</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="needsPartner"
                    checked={newPost.need_partner_response}
                    onChange={(e) => setNewPost({ ...newPost, need_partner_response: e.target.checked })}
                    className="w-4 h-4 sm:w-5 sm:h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="needsPartner" className="text-xs sm:text-sm text-gray-700 cursor-pointer">
                    スマイル・パートナーの回答を希望する
                  </label>
                </div>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowNewPostModal(false)}
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !newPost.title.trim() || !newPost.content.trim()}
                    className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {submitting ? '投稿中...' : '投稿する'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">スマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-xs sm:text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">リンク</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-teal-100">
                <li>
                  <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                    ホーム
                  </Link>
                </li>
                <li>
                  <Link to="/board" className="hover:text-white transition-colors cursor-pointer">
                    みんなの休憩室
                  </Link>
                </li>
                <li>
                  <Link to="/manual" className="hover:text-white transition-colors cursor-pointer">
                    お守りマニュアル
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">お問い合わせ</h4>
              <p className="text-xs sm:text-sm text-teal-100">support@smile-port.jp</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-teal-100">
            <p>
              &copy; 2025 スマイル・ポート. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
